
import React, { useRef, useEffect } from "react";
import { ChatMessage as Message } from "./types";
import { ChatMessage } from "./ChatMessage";
import { useAuth } from "@/context/AuthContext";

interface ChatMessageListProps {
  messages: Message[];
  loading: boolean;
  currentUserId: string;
  onEditStart: (messageId: string, text: string) => void;
  onDelete: (messageId: string) => void;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  loading,
  currentUserId,
  onEditStart,
  onDelete
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Helper functions that were previously being passed as props
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('nl-NL', { 
      hour: '2-digit', 
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getMessageClasses = (message: Message) => {
    let baseClasses = "p-3 rounded-md max-w-[80%]";
    
    if (message.is_deleted) {
      return `${baseClasses} bg-gray-100 italic text-gray-500`;
    }
    
    return `${baseClasses} ${message.user_id === currentUserId 
      ? "bg-green-100" 
      : "bg-blue-100"}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex justify-center items-center h-full text-gray-500">
        Nog geen berichten. Start een gesprek!
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 max-h-[400px] overflow-y-auto">
      {messages.map((message) => (
        <ChatMessage
          key={message.id}
          message={message}
          formatTimestamp={formatTimestamp}
          getInitials={getInitials}
          getMessageClasses={getMessageClasses}
          onEdit={() => {}} // This will be handled via onEditStart
          onDelete={onDelete}
          isCurrentUser={currentUserId === message.user_id}
          editingMessage={null} // This will be handled in parent component
          editText=""  // This will be handled in parent component
          setEditText={() => {}} // This will be handled in parent component
          setEditingMessage={(messageId) => {
            if (messageId) {
              onEditStart(messageId, message.message);
            }
          }}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};
